import axios from "axios";
// Asegúrate de que `useAuthStore` esté disponible o implementa una lógica alternativa si no usas Vuex o Pinia
import { useAuthStore } from "@/stores/auth";
import requestDeduplicator from "@/utils/request-deduplicator";

// Verificar si estamos en producción con las variables de entorno
const isProduction = import.meta.env.VITE_APP_ENV === "production" || true;

const PRODUCTION = {
  api: isProduction ? true : false,
  tienda: isProduction ? true : false,
  caja: isProduction ? true : false,
  billetera: isProduction ? true : false,
};

// ENDPOINTS
const api = axios.create({
  baseURL: PRODUCTION.api
    ? "https://backend.orsai.org/apis"
    : "http://localhost:8080/apis",
  timeout: 60000,
});

const apiTienda = axios.create({
  baseURL: PRODUCTION.tienda
    ? "https://backend.orsai.org/api/tienda"
    : "http://localhost:8083/api/tienda",
  timeout: 60000,
});

const apiCaja = axios.create({
  baseURL: PRODUCTION.caja
    ? "https://backend.orsai.org/api/caja/v1" :
    "http://localhost:6602/api/caja/v1",
  timeout: 60000,
});

const apiBilletera = axios.create({
  baseURL: PRODUCTION.billetera
    ? "https://backend.orsai.org/api/billetera/v1/"
    : "http://localhost:6602/api/billetera/v1",
  timeout: 60000,
});

const apiUsuarios = axios.create({
  baseURL: PRODUCTION.caja
    ? "https://backend.orsai.org/apis/usuarios/v1/"
    : "http://localhost:8080/apis/usuarios/v1/",
  timeout: 60000,
});

// /wm-chorro/v1
const apiTextos = axios.create({
  baseURL: PRODUCTION.api
    ? "https://backend.orsai.org/apis/wm-chorro/v1"
    : "http://localhost:8080/apis/wm-chorro/v1",
  timeout: 60000,
});

// TOKEN
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
  return null;
};

const authInterceptor = (config) => {
  // Obtener token del store
  const authStore = useAuthStore();
  const tokenStore = authStore.token;
  const tokenCookie = getCookie("orsai_token");

  // Priorizar token del store, luego de cookie como respaldo
  const token = tokenStore || tokenCookie;

  if (token && !config.headers["Authorization"]) {
    config.headers["Authorization"] = "Basic " + token;
    console.log('🔐 Token agregado a request:', config.url);
  } else if (!token) {
    console.log('⚠️ No hay token disponible para request:', config.url);
  }
  
  return config;
};

// Interceptor de deduplicación para requests GET
const deduplicationInterceptor = (config) => {
  const key = requestDeduplicator.getRequestKey(config);
  const isGet = (config.method || 'get').toLowerCase() === 'get';
  
  if (isGet) {
    // Marcar el config para que el interceptor de respuesta maneje la deduplicación
    config.__dedupKey = key;
    config.__isGet = true;
  }
  
  return config;
};

// Interceptor de respuesta para manejar deduplicación y cache
const deduplicationResponseInterceptor = (response) => {
  const config = response.config;
  
  if (config.__isGet && config.__dedupKey) {
    // Cachear respuesta exitosa
    requestDeduplicator.setCachedResponse(config.__dedupKey, response);
  }
  
  return response;
};

// Interceptor de request que maneja la deduplicación real
// Usamos un wrapper que intercepta antes de que axios ejecute el request
const createDeduplicationWrapper = (axiosInstance) => {
  const originalRequest = axiosInstance.request.bind(axiosInstance);
  
  axiosInstance.request = function(config) {
    // Normalizar config (puede ser string URL u objeto)
    const normalizedConfig = typeof config === 'string' 
      ? { url: config, method: 'get' } 
      : config;
    
    const key = requestDeduplicator.getRequestKey(normalizedConfig);
    const isGet = (normalizedConfig.method || 'get').toLowerCase() === 'get';
    
    if (isGet) {
      // Verificar cache
      const cached = requestDeduplicator.getCachedResponse(key);
      if (cached) {
        console.log(`[DEDUP] Cache hit: ${normalizedConfig.url || config}`);
        return Promise.resolve(cached);
      }
      
      // Verificar si hay un request pendiente
      if (requestDeduplicator.hasPendingRequest(key)) {
        console.log(`[DEDUP] Reusing pending request: ${normalizedConfig.url || config}`);
        return requestDeduplicator.getPendingRequest(key);
      }
      
      // Crear nuevo request
      const requestPromise = originalRequest(config);
      requestDeduplicator.setPendingRequest(key, requestPromise);
      
      return requestPromise;
    }
    
    return originalRequest(config);
  };
  
  // También interceptar métodos directos (get, post, etc.)
  const originalGet = axiosInstance.get.bind(axiosInstance);
  axiosInstance.get = function(url, config = {}) {
    const fullConfig = { ...config, url, method: 'get' };
    return axiosInstance.request(fullConfig);
  };
  
  return axiosInstance;
};

// Aplicar wrappers de deduplicación
createDeduplicationWrapper(api);
createDeduplicationWrapper(apiTienda);
createDeduplicationWrapper(apiCaja);
createDeduplicationWrapper(apiBilletera);
createDeduplicationWrapper(apiTextos);
createDeduplicationWrapper(apiUsuarios);

// Aplicar interceptores de request (marcar requests GET)
api.interceptors.request.use(deduplicationInterceptor);
apiTienda.interceptors.request.use(deduplicationInterceptor);
apiCaja.interceptors.request.use(deduplicationInterceptor);
apiBilletera.interceptors.request.use(deduplicationInterceptor);
apiTextos.interceptors.request.use(deduplicationInterceptor);
apiUsuarios.interceptors.request.use(deduplicationInterceptor);

// Aplicar interceptores de respuesta (cachear respuestas)
api.interceptors.response.use(deduplicationResponseInterceptor);
apiTienda.interceptors.response.use(deduplicationResponseInterceptor);
apiCaja.interceptors.response.use(deduplicationResponseInterceptor);
apiBilletera.interceptors.response.use(deduplicationResponseInterceptor);
apiTextos.interceptors.response.use(deduplicationResponseInterceptor);
apiUsuarios.interceptors.response.use(deduplicationResponseInterceptor);

// Aplicar interceptor de autenticación (después de deduplicación)
api.interceptors.request.use(authInterceptor);
apiTienda.interceptors.request.use(authInterceptor);
apiCaja.interceptors.request.use(authInterceptor);
apiBilletera.interceptors.request.use(authInterceptor);
apiTextos.interceptors.request.use(authInterceptor);
apiUsuarios.interceptors.request.use(authInterceptor);

// Interceptor de respuesta para manejar errores de autenticación
const responseInterceptor = (response) => {
  return response;
};

const errorInterceptor = (error) => {
  if (error.response && error.response.status === 401) {
    console.log('❌ Error 401 - Token inválido, limpiando sesión');
    const authStore = useAuthStore();
    authStore.cerrarSesion();
    
    // Redirigir al login si no estamos ya ahí
    if (window.location.pathname !== '/ingresar') {
      window.location.href = '/ingresar';
    }
  }
  return Promise.reject(error);
};

// Aplicar interceptores de respuesta
api.interceptors.response.use(responseInterceptor, errorInterceptor);
apiTienda.interceptors.response.use(responseInterceptor, errorInterceptor);
apiCaja.interceptors.response.use(responseInterceptor, errorInterceptor);
apiBilletera.interceptors.response.use(responseInterceptor, errorInterceptor);
apiTextos.interceptors.response.use(responseInterceptor, errorInterceptor);

// Export the custom Axios instance
export { api, apiTienda, apiCaja, apiBilletera, apiUsuarios, apiTextos };
